import { useMultiDoc } from 'global/cnr/contexts/useHelpers/useMultiDoc';
import { convertHelpers } from 'global/common/convert';
import { PopupSelect } from 'global/components/popups/PopupSelect';
import { createRefPath_event } from 'global/firestoreData/appData/appRefPaths';
import _ from 'lodash';
import React, { createContext, Suspense, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { Icon, Label, Sidebar } from 'semantic-ui-react';
import { ParentContext } from '../../../global/cnr/contexts/ParentContext';
import { DimmerPending } from '../../../global/components/alerts/DimmerWrap';
import SuspenseDimmer from '../../../global/components/alerts/SuspenseDimmer';
import UiSaveButtons from '../../../global/components/buttons/UiSaveButtons';
import MenuVert from '../../../global/components/menus/MenuVert';
import DataSidebar from '../../../global/components/swipers/DataSidebar';
import { gEnums } from '../../../global/enums/globalEnums';
import { listYears } from '../../../global/lists/lists';
import { openExternal } from '../../../global/viewSettings/helpers/settingsLinks';
import Wrapper, { wrapperTypes } from '../../../global/wrappers/Wrapper';
import WrapperVertCols from '../../../global/wrappers/WrapperVertCols';
import { SportsDistrictContext } from '../cnr/contexts/SportsDistrictContext';
import { SportsSeasonContext } from '../cnr/contexts/SportsSeasonContext';
import { createMediaSeasonHandlers, createMediaSeasonInitialState, createMediaSeasonReducer } from '../cnr/reducers/CreateMediaSeasonReducer';
import { createScheduleTypes } from '../cnr/reducers/CreateSportsReducer';
import { createSportsSeasonHandlers, createSportsSeasonInitialState, createSportsSeasonReducer } from '../cnr/reducers/CreateSportsSeasonReducer';
import { seasonalUpdateTypes } from '../cnr/reducers/reducerHelpers/seasonalDataHelpers';
import { seasonalHelpers } from '../cnr/reducers/reducerHelpers/seasonalHelpers';
import { dataItems_sportsSeason_create } from '../cnr/reducers/SportsSeasonReducer';
import { _sportCollections } from '../cnr/reducers/SportsSidebarReducer';
import { _sdm } from '../elements/dashboard/SportsDashboard';
import { _fetchModes } from '../elements/matches/MatchDataUpdate';
import { useSportsSeasonData } from '../useHelpers/useSportsSeasonData';
import { useFetchMedia } from '../useHelpers/useFetchMedia';
import { useMedia } from '../useHelpers/useMedia';
import { _allowSportsUpdates, useSeasonalData } from '../useHelpers/useSeasonals';
import { useSportsFilters } from '../useHelpers/useSportsFilters';
import { CreateSportsContext } from './CreateSports';
import { FooterExternal } from './subs/FooterExternal';
import ItemMenuSquares, { _imsTypes } from 'global/wrappers/ItemMenuSquares';
import MatchSquares from '../elements/matches/MatchSquares';
import useGoogleSports from '../useHelpers/useGoogleSports';
import { useSportsTeams } from '../useHelpers/useSportsTeams';
import { useSports } from '../useHelpers/useSports';

export const CreateSportsSeasonContext = createContext();
export const CreateMediaSeasonContext = createContext();

const SportsSeasonDetails = React.lazy(() => import("./subs/SportsSeasonDetails"));
const CreateSportSeasonAlignment = React.lazy(() => import("./CreateSportSeasonAlignment"));
const CreateCrossoverSections = React.lazy(() => import("./subs/CreateCrossoverSections"));
const CreateSportSeasonTeams = React.lazy(() => import("./CreateSportSeasonTeams"));
const CreateSchedule = React.lazy(() => import("./subs/CreateSchedule"));
const CreateSectionDates = React.lazy(() => import("./subs/CreateSectionDates"));

const _steps = {
  year: 'year',
  teams: 'teams',
  alignment: 'alignment',
  sectionCrossovers: 'sectionCrossovers',
  details: 'details',
  scheduleDates: 'scheduleDates',
  schedule: 'schedule',
}

const _rightModes = {
  currentSport: 'currentSport',
  currentSeason: 'currentSeason',
  currentMatches: 'currentMatches',
  currentTeams: 'currentTeams',
  commitCurrent: 'commitCurrent',
  commitSeasonMatches: 'commitSeasonMatches',
  commitSeasonPlayoffMatches: 'commitSeasonPlayoffMatches',
  commitSeasonTeams: 'commitSeasonTeams',
  commitSeasonTeamsAndMatches: 'commitSeasonTeamsAndMatches',
  createMediaSeason: 'createMediaSeason',
  createMediaSeasonAlignment: 'createMediaSeasonAlignment',
  createMediaSeasonFull: 'createMediaSeasonFull',
  createMediaSeasonMatches: 'createMediaSeasonMatches',
  createMediaSeasonRecords: 'createMediaSeasonRecords',
  createMediaSeasonTeams: 'createMediaSeasonTeams',
  exportMatches: 'exportMatches',
  exportPlayoffMatches: 'exportPlayoffMatches',
  exportTeams: 'exportTeams',
  // teamsAndSections: 'teamsAndSections',
}

const _icons = {
  open: 'external alternate'
}

const _sizes = {
  label: 'mini'
}

const CreateSeason = (props) => {

  const _asat = gEnums.availableSubActionTypes

  const { subActionType, handleActionPreview, viewItem } = props

  const _sdh = useSeasonalData()
  const googleSports = useGoogleSports({ sportsDataMode: _sdm.sports.seasonResults })
  const { handleGet_gsMatches } = googleSports ?? {}
  const { get_seasonalData, ammend_sportsSeasonalItem, createSeasonFromPrevious, datasToUpdate, update_seasonalData } = useSportsSeasonData()
  const { get_teamsSport } = useSportsTeams()
  const { getHomeAways } = useSportsFilters()
  const { handleGet_multiDocs } = useMultiDoc()
  const { update_selectedSeason, updating: updating_sportsYear } = useSports()
  const { getMedia_resultsOnly } = useFetchMedia()

  // parentContext
  const parentContext = useContext(ParentContext);
  const { states, fns } = parentContext ?? {}
  const { paps_state } = states ?? {}
  const { database_fns } = fns ?? {}
  const { pathViews } = paps_state ?? {}

  // sportsDistrictContext
  const sportsDistrictContext = useContext(SportsDistrictContext)
  const { sportsDistrict_state } = sportsDistrictContext ?? {}
  const { district_data } = sportsDistrict_state ?? {}
  const { sports, gls_scores } = district_data ?? {}

  // sportsSeasonContext -> is a wrapper around all creates
  const sportsSeasonContext = useContext(SportsSeasonContext)
  const { sportsSeason_state, sportsSeason_handlers } = sportsSeasonContext ?? {}
  const {
    currentSeason_info,
    links_score,
    matches_info,
    parentDataKeys,
    sportPermissions,
    teams_info: teams_info_season,
  } = sportsSeason_state ?? {}

  const { latestSeason, _sportsAbr, _sportsModeKey, _sportsModeSport } = currentSeason_info ?? {}

  // createSportsContext
  const createSportsContext = useContext(CreateSportsContext);
  const { createSports_state } = createSportsContext ?? {}
  const {
    currentSport,
    sportsKey,
    teams,
    teams_info,
    updating: updating_sports,
    _seasonalKey,
  } = createSports_state ?? {}

  const {
    getMedia_alignments,
    getMediaCdm,
    mediaSeason_local,
    mediaResults_local,
    updateMedia_localAlignment,
  } = useMedia({ database_fns, pathViews, links_score, teams, sportsModeSport: _sportsModeSport, sportsKey, sportsAbr: _sportsAbr })

  const init_state_ss = {
    parentDataKeys,
    pathViews,
    currentSeason_info,
    sportsData_info: {
      sports,
      sportPermissions,
      sportsKey,
      _sportsAbr,
      _seasonalKey,
    },
    // mainDb,
  }
  const [createSportsSeason_state, createSportsSeason_dispatch] = useReducer(createSportsSeasonReducer, init_state_ss, createSportsSeasonInitialState)
  const createSportsSeason_handlers = createSportsSeasonHandlers(createSportsSeason_dispatch, createSportsSeason_state)
  const {
    sportsData_info,
    selectedItems,
    _seasonalId,
    sportsSeason_selected,
    sportsSeason_selectedPdks,
    sportsSeason_db,
    updating: updating_create,
    showTeamPaste,
    showSchedulePaste
  } = createSportsSeason_state ?? {}

  const { sportSeasonals } = sportsData_info ?? {}
  const { seasonals } = sportsSeason_selected ?? {}
  const { details, teams: teams_seasonal, matches: matches_seasonal } = seasonals ?? {}
  const { defaultFromLastSeason, seasonStartDate } = details ?? {}

  const { selectedYear, seasonal: selectedSeasonal } = selectedItems ?? {}

  const _sportSeasonalsKeys = sportSeasonals ? _.keys(sportSeasonals) : []

  // local state  
  let steps = []

  switch (subActionType) {
    case _asat.createSeason:
      steps = [
        _steps.teams,
        _steps.alignment,
        _steps.details,
        _steps.sectionCrossovers,
        _steps.scheduleDates,
        _steps.schedule,
      ]
      break;
    case _asat.editSeason:
      steps = [
        _steps.details,
      ]
      break;
  }

  const [currentTeamSport, setCurrentTeamSport] = useState()
  const [mode_right, setMode_right] = useState()
  const [step, setStep] = useState({ index: 0, name: steps[0] })

  useEffect(() => {
    ammend_sportsSeasonalItem(seasonals)
  }, [seasonals]);

  useEffect(() => {
    const cb = (data) => {
      setCurrentTeamSport(data)
    }
    get_teamsSport({ callback: cb })
  }, []);

  useEffect(() => {
    const { teams, seasonal, matches } = datasToUpdate
    const sportInfo = {
      seasonals: seasonal, // YES 
      selectedSeason: selectedYear,
      teams_seasonal: { teams },
    }
    const sportsResultsInfo = {
      matchInfo_seasonal: {
        matches,
      }
    }
    const sportsData = {
      sportInfo,
      sportsResultsInfo
    }
    if (teams && seasonal && matches) {
      sportsSeason_handlers.handleSet_sportsInfoAndResults(sportsData)

    }
  }, [datasToUpdate]);

  const init_state_ms = {
    _sportsAbr,
    database_fns,
    parentDataKeys,
    pathViews,
    sportPermissions,
    sports,
    sportsKey,
    teams_info,
  }

  const updateColor = _allowSportsUpdates.seasonals ? 'green' : 'teal'

  const [createMediaSeason_state, createMediaSeason_dispatch] = useReducer(createMediaSeasonReducer, init_state_ms, createMediaSeasonInitialState)
  const createMediaSeason_handlers = createMediaSeasonHandlers(createMediaSeason_dispatch, createMediaSeason_state)
  const { mediaSeason, mediaSeasonalData_current, updating: updating_media } = createMediaSeason_state

  const _updating = updating_media || updating_create || updating_sports || updating_sportsYear

  const links_sport = gls_scores ? gls_scores[sportsKey] : null
  const { districtLink, googleSheetsId, mediaUrl } = links_sport ?? {}

  const _currentSport = _sportsModeKey ? _.startCase(_sportsModeKey) : currentSport


  const setInit = () => {
    const { sportsTeams } = district_data ?? {}
    const teams_district = _.cloneDeep(sportsTeams?.[sportsKey]?.teams)
    const teams_sport = _.cloneDeep(teams)

    _.forEach(teams_district, (team_district, tdk) => {
      team_district._selected = teams_sport[tdk] ? true : false
    })

    _.forEach(teams_sport, (team_sport) => {
      team_sport._selected = true
    })

    createSportsSeason_handlers.handleAmmend_districtTeams(teams_district)
    createSportsSeason_handlers.handleInit_sportsTeams(teams_sport)
  }

  useEffect(() => {
    getMediaCdm(2026);
    const cbsd = (dataResult) => { createSportsSeason_handlers.handleResults_sportsData(dataResult) }
    const cbss = (dataResult) => { createSportsSeason_handlers.handleResults_sportSeasonals(dataResult) }
    // get the data
    _sdh.createSeasonInit(sportsKey, cbsd) // creates the initial data => cbsd
    _sdh.getSportSeasonals(cbss)
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, []);

  useEffect(() => {
    setInit()
  }, [district_data, teams]);

  useEffect(() => {
    // if 'selectedYear' changes 
    if (selectedYear) {
      const cbn = ({ _seasonalData, seasonalData, _parentDataKeys }) => {
        console.log('seasonalData', seasonalData)
        createSportsSeason_handlers.handleAmmend_sportsSeason_selected(selectedYear, seasonalData, _seasonalData, _parentDataKeys)
      }
      createSportsSeason_handlers.handleStartUpdate()
      createSportsSeason_handlers.handleAmmend_sportsSeason_selected()
      get_seasonalData({ selectedYear, callback: cbn })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [selectedYear]);

  useEffect(() => {
    if (defaultFromLastSeason) {
      const cbls = (res) => { createSportsSeason_handlers.handleResults_seasonalData(res) }
      _sdh.getSeasonalData(sportsKey, latestSeason, cbls)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [defaultFromLastSeason]);

  useEffect(() => {
    if (mediaSeason) {
      createSportsSeason_handlers.handleMedia_ammendSeasonalFromMedia(mediaSeasonalData_current, matches_info)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [mediaSeason]);


  /**
   * Updates the year of the createSportsSeason state
   * @param {item} item  
   */
  const handleActive_year = (item) => createSportsSeason_handlers?.handleAmmend_selectedYearOrSeasonal('selectedYear', item.name)
  const handleActive_seasonal = (value) => createSportsSeason_handlers?.handleAmmend_selectedYearOrSeasonal('seasonal', value)

  // const createGoogleSheet = () => {
  //   fn_cls.createGoogleSpreadsheet('Testing 123').then(response_data => {
  //     const { success } = response_data ?? {}
  //     console.log('response_data', response_data)
  //     console.log('SUCCESS', success)
  //   })
  // }

  const handleClick_getGoogle_results = () => {
    const cbgr = () => { createMediaSeason_handlers.handleStopUpdate() }
    createMediaSeason_handlers.handleStartUpdate()
    const opts = { fetchMode: _fetchModes.mediaResults, latestSeason: selectedYear }
    handleGet_gsMatches({ opts, callback: cbgr })
  }

  const handleClick_getMedia_results = () => {
    createMediaSeason_handlers.handleStartUpdate()
    const callData = {
      year: selectedYear,
      yearAdditional: 0,
      useSingleDate: true,
      singleDate: '02/06/2026'
    }
    getMedia_resultsOnly(callData, true)
    // getMedia_results({ sportsYear: selectedYear, yearAdditional: 0, sportsModeSport: _sportsModeSport, callback: cb1 })
  }

  const handleClick_getMedia_alignments = async () => {
    const cb1 = (r) => {
      const _teamRecords = {}
      const { teamRecords } = r ?? {}
      if (teamRecords) {
        _.forEach(teamRecords, (tr, k) => {
          const _team = _.find(teams, { _itemKey: k })
          _teamRecords[k] = {
            ...tr[selectedYear],
            name: _team?.name
          }
        })
        const _csvr_results = ['name', 'level', 'section', 'record', 'overallRecord']
        const zzz = convertHelpers.convertArrayToCSV(_teamRecords, _csvr_results)
        convertHelpers.copyToClipboard(zzz)
      }

      updateMedia_localAlignment({ sportsKey: sportsKey, year: selectedYear, alignmentResults: r })
      createMediaSeason_handlers.handleStopUpdate()
    }
    createMediaSeason_handlers.handleStartUpdate()
    getMedia_alignments({ sportsYear: selectedYear + 1, callback: cb1 })
  }


  const handleClick_ammendMediaSeason = async () => {
    const _mediaSeason = mediaSeason_local?.data || mediaSeason_local
    const mediaSeason = _mediaSeason?.[selectedYear] || {}
    createSportsSeason_handlers.handleAmmend_sportsSeason_selected(selectedYear, mediaSeason, null, sportsSeason_selectedPdks)
  }

  const handleClick_fixSeasonals = async () => {
    createSportsSeason_handlers.handleFix_seasonals()
  }

  // const handleClick_pasteSeasonals = async () => {
  //   createSportsSeason_handlers.handleShow_teamPaste() 
  // }

  // const handleClick_pasteSchedule = async () => {
  //   createSportsSeason_handlers.handleShow_schedulePaste()
  //   // createSportsSeason_handlers.handleFix_seasonals()
  // }

  const handleCreateSeasonFromPrevious = () => {
    const tms = sportSeasonals?.[selectedYear]?.teams
    const tmsg = _.mapValues(
      _.groupBy(tms, 'levels'),
      (levelGroup) => _.groupBy(levelGroup, 'sections')
    );
    const { homeAways } = getHomeAways({ levelTeams: tmsg })
    createSeasonFromPrevious(sportSeasonals, selectedYear, homeAways, createSportsSeason_handlers.handleSeason_createFromPrevious)
  }

  /**
  * THIS IS THE CLICK FOR THE COMMIT
  * @param {opts} clickOptions 
  */
  const handleClick_commitSeasonal = (clickOptions) => {
    const { updateType } = clickOptions
    handleClick_updateSeasonal(null, null, updateType, true)
  }

  const handleClick_updateSeasonal = (a, b, updateType, isCommit) => {

    const cbu = () => { createSportsSeason_handlers.handleStopUpdate() }

    createSportsSeason_handlers.handleStartUpdate()

    const opts = {
      sportsSeason_selected,
      selectedYear,
      _seasonalKey,
      _sportsAbr,
      sportsKey,
      sportPermissions,
      isCommit
    }

    if (updateType) {
      update_seasonalData({ opts, updateType: updateType, callback: cbu })
      return;
    }
    // return
    switch (selectedSeasonal) {
      case _steps.details:
        update_seasonalData({ opts, updateType: seasonalUpdateTypes.details, callback: cbu })
        break;
      case _steps.teams:
        update_seasonalData({ opts, updateType: seasonalUpdateTypes.teams, callback: cbu })
        break;
      case _steps.alignment:
        update_seasonalData({ opts, updateType: seasonalUpdateTypes.teams, callback: cbu })
        break;
      case _steps.sectionCrossovers:
        update_seasonalData({ opts, updateType: seasonalUpdateTypes.sectionCrossovers, callback: cbu })
        break;
      case _steps.scheduleDates:
        update_seasonalData({ opts, updateType: seasonalUpdateTypes.sectionDates, callback: cbu })
        break;
      case _steps.schedule:
        update_seasonalData({ opts, updateType: seasonalUpdateTypes.matches, callback: cbu })
        break;
      default:
      // nothing
    }
  }

  const handleSet_modeRight = (mr) => setMode_right(mr)
  const handleClick_modeRight = (opts) => handleSet_modeRight(opts.rightMode)

  const externals = {
    sport: () => openExternal.firebaseDbDirect(pathViews, ['sports', sportsKey]),
    sportTeams: () => openExternal.firebaseDbDirect(pathViews, ['_teams_sport', currentTeamSport?.fk]),
    seasonal: () => openExternal.firebaseDbDirect(pathViews, ['_seasonals', sportsSeason_selectedPdks?.seasonals]),
    seasonMatches: () => openExternal.firebaseDbDirect(pathViews, [_sportCollections._matches_seasonal, sportsSeason_selectedPdks?.matches_seasonal]),
    teams: () => openExternal.firebaseDbDirect(pathViews, [_sportCollections._teams_seasonal, sportsSeason_selectedPdks?.teams_seasonal]),
  }

  const buttons_footer = () => {
    const btns_data = []
    const clickOptions_new = { clickType: 'createNew', isNew: true, updateType: 'createNew' }
    const clickOptions_backup = { clickType: 'backupSeason', isBackup: true, updateType: 'backupSeason' }
    const _selectedYearPlusOne = selectedYear + 1

    if (!_.includes(_sportSeasonalsKeys, _selectedYearPlusOne?.toString())) {
      btns_data.push({ oc: handleCreateSeasonFromPrevious, icon: 'arrow right', caption: 'Add New Season From Previous (' + latestSeason + ')', clickOptions: clickOptions_new, color: updateColor })
    }

    btns_data.push({ oc: handleClick_commitSeasonal, icon: 'save', caption: 'Backup Season (' + latestSeason + ')', clickOptions: clickOptions_backup, color: updateColor })
    btns_data.push({ oc: handleClick_fixSeasonals, icon: 'save', caption: 'Fix Seasonals', clickOptions: clickOptions_backup, color: updateColor })

    return { btns_data }
  }

  const btns_seasonal = (btns_right) => {

    const updateCaption = _.isEmpty(sportsSeason_selectedPdks) ? 'Create' : 'Update'

    const caption_update = `${updateCaption} ${selectedYear} Seasonal (${_.startCase(selectedSeasonal || '---')})`

    switch (selectedSeasonal) {
      case _steps.details:
      case _steps.alignment:
      case _steps.sectionCrossovers:
      case _steps.teams:
      case _steps.scheduleDates:
      case _steps.schedule:
        btns_right.push({
          oc: handleClick_updateSeasonal,
          caption: caption_update,
          disabled: !selectedYear,
          color: updateColor,
          icon: 'sun outline'
        })
        break;

      default:
      // nothing
    }
    return btns_right
  }

  const btns_season = (btns_right) => {
    const updateCaption = _.isEmpty(sportsSeason_selectedPdks) ? 'Create' : 'Update'
    const caption_commit = `${updateCaption} ${selectedYear} Season (${_.startCase(selectedSeasonal || '---')})`
    const btn_update = { opts: { rightMode: _rightModes.commitCurrent } }
    switch (selectedSeasonal) {
      case _steps.teams:
        btns_right.push({
          oc: handleClick_modeRight,
          caption: caption_commit,
          disabled: !selectedYear,
          opts: btn_update.opts,
          icon: 'arrow right'
        })
        break;

      case _steps.schedule:
        btns_right.push({
          oc: handleClick_modeRight,
          caption: caption_commit,
          disabled: !selectedYear,
          opts: btn_update.opts,
          icon: 'arrow right'
        })
        break;
      default:
      // nothing
    }
  }

  const footer_main = () => {

    const { btns_data } = buttons_footer()

    const btn_update = { opts: { rightMode: _rightModes.commitCurrent } }

    if (btns_data.length > 0) {

      const btns_right = []

      btns_seasonal(btns_right)
      btns_season(btns_right)

      return <div className={'header-flex'}>
        <FooterExternal
          _icons={_icons}
          _sportCollections={_sportCollections}
          districtLink={districtLink}
          googleSheetsId={googleSheetsId}
          latestSeason={latestSeason}
          mediaUrl={mediaUrl}
          selectedYear={selectedYear}
          sportsKey={sportsKey}
          sportsSeason_selectedPdks={sportsSeason_selectedPdks}
          _sportSeasonalsKeys={_sportSeasonalsKeys}
          updateColor={updateColor}
          pathViews={pathViews}
          handlers={
            {
              handleCreateSeasonFromPrevious,
              handleClick_commitSeasonal,
              handleClick_fixSeasonals,
              handleClick_getMedia_results,
              handleClick_getMedia_alignments,
              handleClick_ammendMediaSeason,
              handleClick_getGoogle_results
            }
          }
        />
        <div className={'header-flex'}>
          <UiSaveButtons others={btns_right} />
        </div>
      </div>
    } else {
      return <UiSaveButtons save={{ caption: 'Update ' + selectedYear + ' Seasonal', oc: handleClick_modeRight, opts: btn_update.opts, color: 'blue', icon: 'arrow right' }} />
    }
  }

  const lblIcon = (caption, data, rm, ocdb, other) => {
    const oc = handleSet_modeRight
    const hasData = !_.isEmpty(data)
    return <div className={`lbl-key ${hasData ? 'hd' : ''}`} onClick={() => { oc && oc(rm) }} title={data}>
      <div>{caption}</div>
      {rm && <div><Icon
        name={ocdb && hasData ? 'external alternate' : 'external alternate'}
        color={ocdb && hasData ? 'orange' : 'purple'}
        onClick={(e) => {
          e.stopPropagation()
          ocdb && ocdb(e)
        }} />
      </div>}
      {other}
    </div>
  }

  const handleSave_selectedSeason = async () => update_selectedSeason({ sportsKey, selectedYear })

  const popupSelect = () => {
    return <PopupSelect items={['Set as Current']} dataItem={{}} handleMenuAction={handleSave_selectedSeason} icon={'ellipsis vertical'} />
  }

  const header_top = useMemo(() => {
    return <div className={'header-flex'}>
      {lblIcon(`Sport - ${_.startCase(_sportsModeSport)} - ${_sportsAbr}`, _currentSport, _rightModes.currentSport, externals.sport)}
      {lblIcon('Sport Teams', currentTeamSport?.fk, _rightModes.currentSeason, externals.sportTeams)}
      {lblIcon('Seasonal', sportsSeason_selectedPdks?.seasonals, _rightModes.currentSeason, externals.seasonal)}
      {lblIcon('Seasonal Teams', sportsSeason_selectedPdks?.teams_seasonal, _rightModes.currentTeams, externals.teams)}
      {lblIcon('Seasonal Matches', sportsSeason_selectedPdks?.matches_seasonal, _rightModes.currentMatches, externals.seasonMatches)}
    </div>
  }, [sportsSeason_selectedPdks, selectedYear])

  const wrapperContent = (content) => <Suspense fallback={<SuspenseDimmer origin={'Create Season'} />}>
    <Wrapper
      content={content}
      wrapperType={wrapperTypes.paddedHeaderAndFooter}
      updating={_updating}
      css={{ container: 'app-dark-mode' }}
    />
  </Suspense>

  const fnAlt_teams = (item) => {
    const { name, sections, levels, position } = item ?? {}
    return <div style={{ display: 'flex' }}>
      <Label size={_sizes.label}>{levels}</Label>
      <Label size={_sizes.label}>{sections}</Label>
      <Label size={_sizes.label}>{position + 1}</Label>
    </div>
  }

  /**
   * 
   * @returns content for the right sidebar
   */
  const content_sidebarRight = () => {

    let headerCaption = _.startCase(mode_right)
    let headerCount = 0;
    let wrapperContent_right;

    let _seasonalUpdateType;

    let _json = sportsSeason_db;

    const { sportsSeason_selected } = createSportsSeason_state ?? {}
    const { seasonals } = sportsSeason_selected ?? {}
    const { matches, teams } = seasonals ?? {}

    switch (selectedSeasonal) {

      case _steps.teams:
        _seasonalUpdateType = seasonalUpdateTypes.fullSeason_teams
        headerCaption = 'Commit Teams'
        headerCount = _.size(teams)
        wrapperContent_right = <ItemMenuSquares
          menuItems={_.sortBy(teams, 'name')}
          iconItem={'users'}
          imsType={_imsTypes.inline}
          fn_altContent={fnAlt_teams}
        />
        break;

      case _steps.schedule:
        const _allMatches = _.cloneDeep(matches)
        seasonalHelpers.ammend_matchLevelsAndSections(_allMatches)
        const _amg = _.groupBy(_allMatches, '_matchCollectionKey')
        wrapperContent_right = <MatchSquares matches={_allMatches} />
        headerCaption = 'Commit Matches'
        headerCount = _.size(_allMatches)
        _json = _allMatches
        _seasonalUpdateType = seasonalUpdateTypes.fullSeason_matches
        break;

      default:
      // nothing
    }

    const clickOptions_save = { updateType: _seasonalUpdateType }

    const _btns = <UiSaveButtons
      save={{
        oc: handleClick_commitSeasonal,
        caption: `${headerCaption} (${_seasonalUpdateType})`,
        color: updateColor,
        opts: clickOptions_save
      }} />

    const header_wrapper = <div className='header-flex'>
      <div>{headerCaption}</div>
      <Label size={_sizes.label}>{headerCount}</Label>
    </div>

    return <Wrapper
      header={header_wrapper}
      content={wrapperContent_right}
      footer={_btns}
      wrapperType={wrapperTypes.paddedHeaderAndFooter}
      updating={_updating}
    />
  }

  /**
   * 
   * @param {*} visible 
   * @returns DateSidebar for the right content (if visible)
   */
  const sidebar_right = (visible) => <DataSidebar
    direction={'right'}
    visible={visible}
    content={visible && content_sidebarRight()}
    onHide={handleSet_modeRight}
  />

  const sidebar = (content) => <Sidebar.Pushable style={{ overflow: 'hidden' }}>
    {sidebar_right(mode_right ? true : false)}
    <Sidebar.Pusher dimmed={mode_right ? true : false} className={'h100'}>
      {wrapperContent(content)}
    </Sidebar.Pusher>
  </Sidebar.Pushable>

  const content = () => {
    switch (selectedSeasonal) {
      case _steps.details:
        return sidebar(<SportsSeasonDetails subActionType={subActionType} currentOnly={false} />)
      case _steps.teams:
        return sidebar(<CreateSportSeasonTeams />)
      case _steps.alignment:
        return sidebar(<CreateSportSeasonAlignment />)
      case _steps.sectionCrossovers:
        return sidebar(<CreateCrossoverSections />)
      case _steps.scheduleDates:
        return sidebar(<CreateSectionDates createScheduleType={createScheduleTypes.season} teamCount={step.name} />)
      case _steps.schedule:
        return sidebar(<CreateSchedule viewItem={viewItem} handleActionPreview={handleActionPreview} />)
      default:
        return <div></div>
    }
  }

  let cn_container = 'create-season'
  if (_seasonalId) { cn_container += ' verif' }

  /**
   * menu containing a list of years.
   * click triggers handleActive_year
   */
  const menu_vert_years = useMemo(() => {

    const dataMenuItems = subActionType === _asat.editSeason ? [{ name: latestSeason }] : listYears(1, 10, false, true)

    // the NAME is the value passed into handleActive_year/handleAmmend_selectedYearOrSeasonal
    _.forEach(dataMenuItems, (dmi) => {
      const hasSeasonal = sportSeasonals && sportSeasonals[dmi?.name] ? true : false
      const hasMedia = mediaResults_local && mediaResults_local?.[dmi?.name] ? true : false
      if (hasSeasonal && hasMedia) {
        dmi.icon = 'check square'
        dmi.iconColor = 'green'
      } else if (hasSeasonal) {
        dmi.icon = 'check circle'
        dmi.iconColor = 'green'
      } else if (hasMedia) {
        dmi.icon = 'check circle outline'
        dmi.iconColor = 'green'
      }
    })


    return <Wrapper
      header={'Year'}
      content={<ItemMenuSquares menuItems={dataMenuItems} activeItem={selectedYear} imsType={_imsTypes.inline} onClick={handleActive_year} />}
    />
  }, [selectedYear, sportSeasonals, mediaResults_local, showTeamPaste, showSchedulePaste])

  const fnAlt_s = (item) => {
    switch (item) {
      case 'teams':
        return <Label size={_sizes.label} color='blue'>{_.size(teams_seasonal)}</Label>
      case 'schedule':
        return <Label size={_sizes.label} color='blue'>{_.size(matches_seasonal)}</Label>
    }
  }

  const menu_vert_steps = useMemo(() => {
    return <Wrapper
      header={<div className='header-flex'>
        <div>{`Seasonal - ${selectedYear ? selectedYear : 'None'}`}</div>
        <div>{popupSelect()}</div>
      </div>}
      content={<ItemMenuSquares
        menuItems={steps}
        activeItem={selectedSeasonal}
        imsType={_imsTypes.inline}
        onClick={handleActive_seasonal}
        fn_altContent={fnAlt_s}
      />}
      updating={_updating}
    />
  }, [selectedSeasonal, selectedYear, showTeamPaste, showSchedulePaste, teams_seasonal])

  const wrapper_main = () => <Wrapper
    content={content()}
  />

  // contents. horizontal
  const wvc_season = () => <WrapperVertCols mvc={`season`}  >
    {menu_vert_years}
    {selectedYear && menu_vert_steps}
    {selectedYear && wrapper_main()}
  </WrapperVertCols>

  // main wrapper. vertical
  const content_sport = () => <Wrapper
    header={header_top}
    content={wvc_season()}
    footer={footer_main()}
    wrapperType={wrapperTypes.paddedHeaderAndFooter}
    // updating={_updating}
    css={{ container: 'app-dark-mode' }}
  />

  const contentWithProvider = () => <CreateSportsSeasonContext.Provider value={{ createSportsSeason_state, createSportsSeason_handlers }}>
    <CreateMediaSeasonContext.Provider value={{ createMediaSeason_state, createMediaSeason_handlers }}>
      {content_sport()}
    </CreateMediaSeasonContext.Provider>
  </CreateSportsSeasonContext.Provider>

  return step ? contentWithProvider() : <DimmerPending />
}

export default CreateSeason